import React from "react";
import { PROBES, VARIANTS } from "../../data/build";

function Badge({ x, y, n, r = 12 }) {
  return (
    <g>
      <circle cx={x} cy={y} r={r} fill="#0F172A" />
      <text x={x} y={y + r * 0.34} textAnchor="middle" fill="#7DD3FC" fontFamily="JetBrains Mono, monospace" fontWeight="700" fontSize={r - 1}>{n}</text>
    </g>
  );
}
const dimText = { fontFamily: "JetBrains Mono, monospace", fontSize: 12, fill: "#0369A1" };
const lbl = { fontFamily: "Plus Jakarta Sans, sans-serif", fontWeight: 700, fontSize: 12.5, fill: "#0F172A" };
const cap = { fontFamily: "Plus Jakarta Sans, sans-serif", fontWeight: 600, fontSize: 11.5, fill: "#0F172A" };
const mono = { fontFamily: "JetBrains Mono, monospace", fontSize: 10.5, fill: "#64748B" };
const slotTxt = { fontFamily: "JetBrains Mono, monospace", fontWeight: 700, fontSize: 8.5, fill: "#0369A1" };

// A = 6-in-1, B = phosphate, C = magnesium. Demo: A lit.
const LANES = [
  { k: "A", label: "6 IN 1", pad: "#F59E0B", lit: true },
  { k: "B", label: "PHOSPHATE", pad: "#60A5FA", lit: false },
  { k: "C", label: "MAGNESIUM", pad: "#DB2777", lit: false },
];
const Led = ({ x, y, on }) => <circle cx={x} cy={y} r="4" fill={on ? "#22D3EE" : "#CBD5E1"} stroke={on ? "#0891B2" : "#94A3B8"} strokeWidth="1" />;

/* ---------------- Dimensioned General Arrangement ---------------- */
export function TesterGA() {
  return (
    <div className="overflow-x-auto">
      <svg viewBox="0 0 1180 560" className="w-full min-w-[820px] h-auto" role="img" aria-label="Dimensioned general arrangement">
        {/* ---- FRONT ---- */}
        <text x="200" y="46" textAnchor="middle" style={lbl}>FRONT ELEVATION</text>
        <path d="M92,96 L64,66 Q200,52 336,66 L308,96 Z" fill="#E0F2FE" stroke="#0EA5E9" strokeWidth="2" />
        <text x="200" y="84" textAnchor="middle" style={{ ...dimText, fill: "#0EA5E9" }}>spill-proof catch basin</text>
        <rect x="92" y="96" width="216" height="300" rx="16" fill="#FFFFFF" stroke="#334155" strokeWidth="2.5" />
        <rect x="120" y="122" width="160" height="86" rx="8" fill="#0F172A" />
        <circle cx="200" cy="165" r="22" fill="none" stroke="#38BDF8" strokeWidth="4" />
        <rect x="120" y="222" width="160" height="8" rx="4" fill="#38BDF8" />
        <circle cx="266" cy="262" r="8" fill="none" stroke="#38BDF8" strokeWidth="2.5" />
        <line x1="266" y1="256" x2="266" y2="263" stroke="#38BDF8" strokeWidth="2.5" />
        <line x1="92" y1="410" x2="308" y2="410" stroke="#0369A1" strokeWidth="1" />
        <text x="200" y="430" textAnchor="middle" style={dimText}>260 mm</text>
        <line x1="78" y1="96" x2="78" y2="396" stroke="#0369A1" strokeWidth="1" />
        <text x="52" y="248" textAnchor="middle" style={dimText} transform="rotate(-90 52 248)">300 mm</text>
        <text x="200" y="452" textAnchor="middle" style={{ ...dimText, fill: "#64748B" }}>3 strips load from top · prompt LED per slot</text>

        {/* ---- SIDE ---- */}
        <text x="560" y="46" textAnchor="middle" style={lbl}>SIDE</text>
        <path d="M470,96 L470,72 Q560,58 650,72 L650,96 Z" fill="#E0F2FE" stroke="#0EA5E9" strokeWidth="2" />
        <path d="M470,96 Q470,80 486,80 L634,80 Q650,80 650,96 L650,376 Q650,396 630,396 L490,396 Q470,396 470,376 Z" fill="#FFFFFF" stroke="#334155" strokeWidth="2.5" />
        {[486, 498, 510].map((x) => <rect key={x} x={x} y="66" width="4" height="16" rx="2" fill="#0F172A" />)}
        <line x1="486" y1="62" x2="452" y2="52" stroke="#94A3B8" strokeWidth="1" />
        <text x="448" y="50" textAnchor="end" style={mono}>3 × 6 mm slots (front)</text>
        <line x1="470" y1="412" x2="650" y2="412" stroke="#0369A1" strokeWidth="1" />
        <text x="560" y="432" textAnchor="middle" style={dimText}>240 mm deep</text>
        <text x="560" y="454" textAnchor="middle" style={{ ...dimText, fill: "#64748B" }}>basin drains + flood-rinses to tank</text>

        {/* ---- TOP ---- */}
        <text x="900" y="46" textAnchor="middle" style={lbl}>TOP (basin rear · 3 slots front)</text>
        <rect x="760" y="66" width="280" height="230" rx="18" fill="#FFFFFF" stroke="#334155" strokeWidth="2.5" />
        {/* flood-rinse gutter ring */}
        <ellipse cx="900" cy="138" rx="104" ry="60" fill="none" stroke="#0EA5E9" strokeWidth="1.4" strokeDasharray="5 4" />
        <text x="1010" y="96" textAnchor="end" style={{ ...mono, fill: "#0EA5E9" }}>flood gutter (22)</text>
        {/* basin */}
        <path d="M812,88 Q900,78 988,88 L978,190 Q900,202 822,190 Z" fill="#E0F2FE" stroke="#0EA5E9" strokeWidth="2" />
        <text x="900" y="100" textAnchor="middle" style={{ ...mono, fill: "#0EA5E9" }}>catch basin (no spout)</text>
        <circle cx="900" cy="140" r="13" fill="#BAE6FD" stroke="#0369A1" strokeWidth="2" /><circle cx="900" cy="140" r="4" fill="#0369A1" />
        <path d="M874,192 Q900,204 926,192" fill="none" stroke="#0EA5E9" strokeWidth="3" />
        <text x="900" y="216" textAnchor="middle" style={{ ...mono, fill: "#0EA5E9" }}>beaker notch</text>
        {/* 3 slots front + prompt LEDs + labels */}
        {LANES.map((L, i) => {
          const x = 826 + i * 74;
          return (
            <g key={L.k}>
              <rect x={x} y="234" width="6" height="20" rx="2" fill="#0F172A" />
              <Led x={x + 3} y={266} on={L.lit} />
              <text x={x + 3} y={284} textAnchor="middle" style={slotTxt}>{L.label}</text>
            </g>
          );
        })}

        <text x="70" y="524" style={{ ...dimText, fill: "#475569" }}>Envelope proposed at 260 × 300 × 240 mm — schematic, to be validated in DFM. ● lit prompt LED = insert that strip.</text>
      </svg>
    </div>
  );
}

/* ---------------- Internal cutaway ---------------- */
export function TesterCutaway() {
  return (
    <div className="overflow-x-auto">
      <svg viewBox="0 0 940 680" className="w-full min-w-[780px] h-auto" role="img" aria-label="Internal cutaway, cover removed">
        <rect x="30" y="34" width="880" height="620" rx="22" fill="#FFFFFF" stroke="#334155" strokeWidth="3" />
        <text x="470" y="24" textAnchor="middle" style={lbl}>INTERNAL CUTAWAY · 3 SLOTS SUBMERGED · FLOOD-RINSE SELF-CLEAN</text>

        {/* DRY ELECTRONICS */}
        <rect x="640" y="52" width="252" height="520" rx="14" fill="#FFFBEB" stroke="#FDE68A" strokeWidth="2" />
        <text x="766" y="74" textAnchor="middle" style={{ ...dimText, fill: "#B45309" }}>DRY ELECTRONICS</text>
        {[["3", "Touchscreen + prompt LEDs (23)", 88], ["14", "MCU / control PCB", 164], ["15", "Analog + optical AFE", 240], ["16", "USB-C + Bluetooth", 316], ["18", "Charger board", 392], ["17", "Li-ion battery", 468]].map(([n, t, y]) => (
          <g key={n}>
            <rect x="664" y={y} width="206" height="56" rx="8" fill="#FFFFFF" stroke="#F59E0B" strokeWidth="1.5" />
            <text x="722" y={Number(y) + 33} style={{ fontFamily: "Plus Jakarta Sans", fontWeight: 600, fontSize: 11.5, fill: "#0F172A" }}>{t}</text>
            <Badge x={680} y={Number(y) + 28} n={n} r={11} />
          </g>
        ))}

        {/* WET / MEASUREMENT */}
        <rect x="48" y="52" width="572" height="520" rx="14" fill="#F0F9FF" stroke="#BAE6FD" strokeWidth="2" />
        <text x="70" y="74" style={{ ...dimText, fill: "#0369A1" }}>WET / MEASUREMENT PATH</text>

        {/* basin (left) + flood gutter (22) */}
        <path d="M110,96 L92,96 Q108,116 128,120 L300,120 Q320,116 330,96 L312,96 Z" fill="#E0F2FE" stroke="#0EA5E9" strokeWidth="2" />
        <path d="M92,96 Q92,84 108,84 L314,84 Q330,84 330,96" fill="none" stroke="#0EA5E9" strokeWidth="2" />
        <text x="200" y="108" textAnchor="middle" style={{ ...cap, fill: "#0369A1" }}>Catch basin (no spout)</text>
        <Badge x={302} y={100} n="5" r={11} />
        {/* gutter channel around basin */}
        <path d="M88,96 Q88,78 106,78 L316,78 Q334,78 334,96" fill="none" stroke="#0EA5E9" strokeWidth="1.4" strokeDasharray="4 3" />
        <Badge x={92} y={78} n="22" r={10} />
        {/* basin drain */}
        <path d="M196,120 L216,120 L206,148 Z" fill="#BAE6FD" stroke="#0EA5E9" strokeWidth="1.5" />

        {/* 3 slots on front deck + prompt LEDs + present sensors */}
        {LANES.map((L, i) => {
          const x = 348 + i * 30;
          return (
            <g key={L.k}>
              <Led x={x + 3} y={92} on={L.lit} />
              <rect x={x} y="104" width="6" height="16" fill="#0F172A" />
              <circle cx={x + 3} cy="126" r="3" fill="#22D3EE" />
              <text x={x + 3} y={150} textAnchor="middle" style={{ ...slotTxt, fontSize: 8 }}>{L.k}</text>
            </g>
          );
        })}
        <text x="500" y="96" style={{ ...mono, fill: "#0369A1" }}>prompt LEDs (23)</text>
        <text x="500" y="126" style={{ ...mono, fill: "#0891B2" }}>present sensors ×3 (10)</text>

        {/* sample tank */}
        <rect x="150" y="160" width="290" height="200" rx="10" fill="#FFFFFF" stroke="#0891B2" strokeWidth="2.5" />
        <rect x="160" y="216" width="270" height="134" rx="6" fill="#CFF0FF" />
        <line x1="160" y1="216" x2="430" y2="216" stroke="#7DD3FC" strokeWidth="1.4" strokeDasharray="4 3" />
        <rect x="404" y="200" width="10" height="150" fill="#94A3B8" />
        <path d="M414,216 L446,216 L446,350 L430,350 Z" fill="#DBEAFE" stroke="#93C5FD" strokeWidth="1.5" />
        <text x="452" y="272" style={{ ...mono, fill: "#0891B2" }}>weir →</text>
        <Badge x={166} y={176} n="6" r={10} />
        <text x="186" y="180" style={{ ...cap, fill: "#0369A1" }}>Overflow-weir sample tank</text>

        {/* probes */}
        {PROBES.map((pr, i) => (
          <g key={pr.p}>
            <line x1={186 + i * 28} y1="160" x2={186 + i * 28} y2="330" stroke={pr.color} strokeWidth="5" strokeLinecap="round" />
            <circle cx={186 + i * 28} cy="334" r="6" fill={pr.color} />
          </g>
        ))}
        <Badge x={196} y={392} n="7" r={10} />
        <text x="216" y="396" style={cap}>Probe block · FC · pH · Salt · Temp (submerged)</text>

        {/* 3 strips submerged + readers */}
        {LANES.map((L, i) => {
          const x = 348 + i * 30;
          return (
            <g key={L.k}>
              <rect x={x} y="120" width="6" height="220" rx="1" fill="#334155" />
              <rect x={x} y="300" width="6" height="40" fill={L.pad} />
              <circle cx={x + 3} cy="270" r="6" fill="#ECFEFF" stroke="#0891B2" strokeWidth="1.4" />
              <circle cx={x + 3} cy="270" r="2" fill="#0891B2" />
            </g>
          );
        })}
        <Badge x={352} y={430} n="8" r={9} /><Badge x={382} y={430} n="9" r={9} /><Badge x={412} y={430} n="21" r={9} />
        <text x="382" y="452" textAnchor="middle" style={{ ...mono, fill: "#334155" }}>strips A · B · C (6 mm) + optical readers</text>

        {/* fluidics deck */}
        {[["11", "Flush / flood-rinse pump", 72], ["12", "Rinse source *", 246], ["13", "Waste path *", 420]].map(([n, t, x]) => (
          <g key={n}>
            <rect x={x} y="486" width="160" height="52" rx="8" fill="#ECFEFF" stroke="#22D3EE" strokeWidth="1.5" />
            <text x={Number(x) + 80} y="517" textAnchor="middle" style={{ fontFamily: "Plus Jakarta Sans", fontWeight: 600, fontSize: 11, fill: "#0F172A" }}>{t}</text>
            <Badge x={Number(x) + 16} y="498" n={n} r={10} />
          </g>
        ))}
        {/* flood-rinse up arrow */}
        <path d="M108,486 L108,140" fill="none" stroke="#0EA5E9" strokeWidth="2" strokeDasharray="6 4" markerEnd="url(#up)" />
        <defs><marker id="up" markerWidth="10" markerHeight="10" refX="4" refY="7" orient="auto"><path d="M4,0 L8,8 L0,8 Z" fill="#0EA5E9" /></marker></defs>
        <text x="80" y="320" style={{ ...mono, fill: "#0EA5E9" }} transform="rotate(-90 80 320)">flood-rinse ↑ cleans basin</text>

        <text x="70" y="612" style={{ ...mono, fill: "#64748B" }}>* Rinse + waste differ by variant — countertop plumbs tap + drain; portable uses 1.5 L internal rinse & waste bottles.</text>
      </svg>
    </div>
  );
}

/* ---------------- Exploded stack ---------------- */
export function TesterExploded() {
  const layers = [
    { n: "5·22", t: "Catch basin + flood-rinse gutter", note: "no spout · self-cleaning" },
    { n: "6", t: "Overflow-weir sample tank", note: "self-levelling · submerged read" },
    { n: "7", t: "Probe block (×4)", note: "FC · pH · Salt/TDS · Temp" },
    { n: "8·9·21·10", t: "3 strip slots + readers", note: "6-in-1 · phosphate · magnesium · auto-detect" },
    { n: "11·12·13", t: "Fluidics deck", note: "flood pump · rinse · waste" },
    { n: "3·4·23", t: "Screen · status LED · prompt LEDs", note: "guided strip prompt" },
    { n: "14·15·16", t: "PCB stack", note: "MCU · AFE · comms" },
    { n: "17·18", t: "Battery + charger", note: "portable power" },
    { n: "2", t: "Chassis / wet-dry divider", note: "" },
    { n: "1·19", t: "Outer shell + service cover", note: "" },
    { n: "20", t: "Base + feet", note: "" },
  ];
  const H = 56, gap = 18, x = 300, w = 340;
  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 780 ${layers.length * (H + gap) + 40}`} className="w-full min-w-[580px] h-auto" role="img" aria-label="Exploded view">
        <line x1={x + w / 2} y1="20" x2={x + w / 2} y2={layers.length * (H + gap) + 10} stroke="#CBD5E1" strokeWidth="2" strokeDasharray="4 5" />
        {layers.map((l, i) => {
          const y = 20 + i * (H + gap);
          return (
            <g key={l.n}>
              <rect x={x} y={y} width={w} height={H} rx="10" fill="#FFFFFF" stroke="#0891B2" strokeWidth="2" />
              <Badge x={x + 26} y={y + H / 2} n={l.n} r={13} />
              <text x={x + 56} y={y + 24} style={{ fontFamily: "Plus Jakarta Sans", fontWeight: 700, fontSize: 12.5, fill: "#0F172A" }}>{l.t}</text>
              <text x={x + 56} y={y + 42} style={{ fontFamily: "JetBrains Mono", fontSize: 10, fill: "#64748B" }}>{l.note}</text>
              <line x1={x + w} y1={y + H / 2} x2={x + w + 40} y2={y + H / 2} stroke="#7DD3FC" strokeWidth="1.5" />
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ---------------- Variant plumbing ---------------- */
export function TesterVariants() {
  const box = (x, v) => (
    <g key={v.key}>
      <rect x={x} y="40" width="380" height="300" rx="16" fill="#FFFFFF" stroke={v.color} strokeWidth="2" />
      <rect x={x + 20} y="24" width="160" height="30" rx="15" fill={v.color} />
      <text x={x + 100} y="44" textAnchor="middle" style={{ fontFamily: "Plus Jakarta Sans", fontWeight: 700, fontSize: 13, fill: "#FFFFFF" }}>{v.name}</text>
      <rect x={x + 140} y="140" width="100" height="90" rx="8" fill="#CFF0FF" stroke="#0891B2" strokeWidth="2" />
      <text x={x + 190} y="192" textAnchor="middle" style={{ ...mono, fill: "#0369A1" }}>tank</text>
      {v.key === "counter" ? (
        <>
          <rect x={x + 24} y="100" width="80" height="40" rx="8" fill="#E0F7FA" stroke="#0891B2" strokeWidth="1.5" />
          <text x={x + 64} y="124" textAnchor="middle" style={mono}>tap in</text>
          <line x1={x + 104} y1="120" x2={x + 140} y2="160" stroke="#0891B2" strokeWidth="3" markerEnd="url(#arrow)" />
          <rect x={x + 276} y="240" width="80" height="40" rx="8" fill="#F1F5F9" stroke="#64748B" strokeWidth="1.5" />
          <text x={x + 316} y="264" textAnchor="middle" style={mono}>drain</text>
          <line x1={x + 240} y1="215" x2={x + 276} y2="255" stroke="#64748B" strokeWidth="3" markerEnd="url(#arrow)" />
          <text x={x + 190} y="316" textAnchor="middle" style={{ ...cap, fill: v.color }}>Plumbed: tap rinse → drain</text>
        </>
      ) : (
        <>
          <rect x={x + 26} y="86" width="74" height="64" rx="8" fill="#E0F7FA" stroke="#0891B2" strokeWidth="1.5" />
          <text x={x + 63} y="112" textAnchor="middle" style={mono}>1.5 L</text>
          <text x={x + 63} y="126" textAnchor="middle" style={mono}>rinse</text>
          <line x1={x + 100} y1="130" x2={x + 140} y2="165" stroke="#0891B2" strokeWidth="3" markerEnd="url(#arrow)" />
          <rect x={x + 280} y="226" width="74" height="64" rx="8" fill="#F3F0FF" stroke="#7C3AED" strokeWidth="1.5" />
          <text x={x + 317} y="252" textAnchor="middle" style={mono}>1.5 L</text>
          <text x={x + 317} y="266" textAnchor="middle" style={mono}>waste</text>
          <line x1={x + 240} y1="210" x2={x + 280} y2="250" stroke="#7C3AED" strokeWidth="3" markerEnd="url(#arrow)" />
          <text x={x + 190} y="316" textAnchor="middle" style={{ ...cap, fill: v.color }}>Self-contained: refill rinse · empty waste</text>
        </>
      )}
    </g>
  );
  return (
    <div className="overflow-x-auto">
      <svg viewBox="0 0 840 360" className="w-full min-w-[720px] h-auto" role="img" aria-label="Variant plumbing diagrams">
        <defs>
          <marker id="arrow" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><path d="M0,0 L8,4 L0,8 Z" fill="#334155" /></marker>
        </defs>
        {box(20, VARIANTS[0])}
        {box(440, VARIANTS[1])}
      </svg>
    </div>
  );
}
